// @ts-nocheck
import { isNonEmptyString } from "@insite/client-framework/Common/Utilities/StringUtilities";
import translate from "@insite/client-framework/Translate";
import { CountryModel, PaymentMethodDto } from "@insite/client-framework/Types/ApiModels";
import { PaymentDetailsCartState } from "@insite/content-library/Hooks/Payments/usePaymentDetailsCartState";
import { UsePaymentDetailsFormState } from "@insite/content-library/Hooks/Payments/usePaymentDetailsFormState";
import { show } from "@insite/mobius/Overlay/helpers/ariaAppHider";

export interface UsePaymentDetailsFormValidation {
    validatePaymentMethod: (paymentMethod: string) => boolean;
    validatePONumber: (poNumber: string) => boolean;
    validateAddress1: (address1: string) => boolean;
    validateCountry: (countryId: string) => boolean;
    validateState: (stateId: string) => boolean;
    validateCity: (city: string) => boolean;
    validatePostalCode: (postalCode: string) => boolean;
}
/* Bunzl Custom Code Start */
interface cartReplacements {
    paymentMethods: PaymentMethodDto[] | null | undefined;
    isCreditCard: boolean;
    isECheck: boolean;
    selectedCountry?: CountryModel;
    showPoNumber: boolean;
    requiresPoNumber: boolean;
}
/* Bunzl Custom Code End */

function usePaymentDetailsFormValidation(
    paymentDetailsFormState: UsePaymentDetailsFormState,
    cartStateOrReplacement: cartReplacements | PaymentDetailsCartState,
): UsePaymentDetailsFormValidation {
    /* Bunzl Custom Code Start */
    const { paymentMethods, isCreditCard, isECheck, selectedCountry } = cartStateOrReplacement;
    const cart = "paymentMethods" in cartStateOrReplacement ? undefined : cartStateOrReplacement.cart;
    let showPoNumber = false;
    let requiresPoNumber = false;
    if (cart) {
        showPoNumber = cart.showPoNumber;
        requiresPoNumber = cart.requiresPoNumber;
    }
    if ("showPoNumber" in cartStateOrReplacement) {
        showPoNumber = cartStateOrReplacement.showPoNumber;
        requiresPoNumber = cartStateOrReplacement.requiresPoNumber;
    }
    /* Bunzl Custom Code End */

    const { useBillingAddress, isPayPal } = paymentDetailsFormState;
    const validatePaymentMethod = (paymentMethod: string) => {
        const paymentMethodValid =
            isPayPal || !paymentMethods || paymentMethods.length === 0 || isNonEmptyString(paymentMethod);
        paymentDetailsFormState.setPaymentMethodError(
            !paymentMethodValid ? translate("Payment Method is required.") : "",
        );
        return paymentMethodValid;
    };

    const validatePONumber = (poNumber: string) => {
        // Bunzl Custom Code
        const poNumberValid = showPoNumber || requiresPoNumber || isNonEmptyString(poNumber);
        paymentDetailsFormState.setPONumberError(!poNumberValid ? translate("PO Number is required.") : "");
        return poNumberValid;
    };

    const validateAddress1 = (address1: string) => {
        const address1Valid =
            !(isCreditCard || isECheck) || useBillingAddress || (!useBillingAddress && isNonEmptyString(address1));
        paymentDetailsFormState.setAddress1Error(!address1Valid ? translate("Address is required.") : "");
        return address1Valid;
    };

    const validateCountry = (countryId: string) => {
        const countryValid =
            !(isCreditCard || isECheck) || useBillingAddress || (!useBillingAddress && isNonEmptyString(countryId));
        paymentDetailsFormState.setCountryError(!countryValid ? translate("Country is required.") : "");
        return countryValid;
    };

    const validateState = (stateId: string) => {
        const stateValid =
            (!!selectedCountry && !!selectedCountry.states && selectedCountry.states.length <= 0) ||
            !(isCreditCard || isECheck) ||
            useBillingAddress ||
            (!useBillingAddress && isNonEmptyString(stateId));
        paymentDetailsFormState.setStateError(!stateValid ? translate("State is required.") : "");
        return stateValid;
    };

    const validateCity = (city: string) => {
        const cityValid =
            !(isCreditCard || isECheck) || useBillingAddress || (!useBillingAddress && isNonEmptyString(city));
        paymentDetailsFormState.setCityError(!cityValid ? translate("City is required.") : "");
        return cityValid;
    };

    const validatePostalCode = (postalCode: string) => {
        const postalCodeValid =
            !(isCreditCard || isECheck) || useBillingAddress || (!useBillingAddress && isNonEmptyString(postalCode));
        paymentDetailsFormState.setPostalCodeError(!postalCodeValid ? translate("Postal Code is required.") : "");
        return postalCodeValid;
    };

    return {
        validatePaymentMethod,
        validatePONumber,
        validateAddress1,
        validateCountry,
        validateState,
        validateCity,
        validatePostalCode,
    };
}

export default usePaymentDetailsFormValidation;
